import type { RuleKey } from '../safety/safety-types';

export type { RuleKey };

export interface HeatPoint {
  latitude: number;
  longitude: number;
  ruleKey: RuleKey;
  deviceId: string;
  driverId: string | null;
  timestamp: string;
  intensity: number;
}

export interface DangerZone {
  id: string;
  centroidLat: number;
  centroidLng: number;
  radiusM: number;
  totalViolations: number;
  dominantRule: RuleKey;
  byRule: Partial<Record<RuleKey, number>>;
  uniqueDeviceIds: Set<string>;
  uniqueDriverIds: Set<string>;
  locationName: string | null;
  peakHour: number | null;
  rank: number;
  points: HeatPoint[];
}

export interface LiveVehicleDot {
  deviceId: string;
  deviceName: string;
  latitude: number;
  longitude: number;
  speed: number;
  isDriving: boolean;
  healthStatus: 'healthy' | 'warning' | 'critical';
}

export interface LiveViolation {
  id: string;
  deviceName: string;
  driverName: string | null;
  ruleKey: RuleKey;
  activeFrom: string;
  latitude: number | null;
  longitude: number | null;
}

export interface HeatmapAnalysisParams {
  fromISO: string;
  toISO: string;
  deviceIds: Set<string>;
  ruleIdsMap: Map<RuleKey, string[]>;
}

export type TimeSlot = 'all' | 'morningRush' | 'midday' | 'eveningRush' | 'night';
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const RULE_KEY_ORDER: RuleKey[] = [
  'hardAccel', 'harshBraking', 'harshCornering', 'speeding', 'seatBelt', 'idling',
];

export interface RuleVisual {
  label: string;
  color: string;
  heatColors: [string, string, string];
  icon: string;
}

export const RULE_VISUALS: Record<RuleKey, RuleVisual> = {
  hardAccel: {
    label: 'Hard Accel', color: '#ff2055',
    heatColors: ['rgba(255,32,85,0)', '#ff4070', '#ff2055'],
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>',
  },
  harshBraking: {
    label: 'Harsh Braking', color: '#ff7730',
    heatColors: ['rgba(255,119,48,0)', '#ff9960', '#ff7730'],
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.6" fill="currentColor"/></svg>',
  },
  harshCornering: {
    label: 'Cornering', color: '#ffcc00',
    heatColors: ['rgba(255,204,0,0)', '#ffdd55', '#ffcc00'],
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 10 20 15 15 20"/><path d="M4 4v7a4 4 0 0 0 4 4h12"/></svg>',
  },
  speeding: {
    label: 'Speeding', color: '#9c40ff',
    heatColors: ['rgba(156,64,255,0)', '#b470ff', '#9c40ff'],
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a10 10 0 0 1 7.38 16.75"/><path d="M12 2a10 10 0 0 0-7.38 16.75"/><path d="m12 12-2.5-4.5"/><circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none"/></svg>',
  },
  seatBelt: {
    label: 'Seat Belt', color: '#4f8fff',
    heatColors: ['rgba(79,143,255,0)', '#7aabff', '#4f8fff'],
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
  },
  idling: {
    label: 'Idling', color: '#10ffb0',
    heatColors: ['rgba(16,255,176,0)', '#40ffcc', '#10ffb0'],
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
  },
};
